import {
  Habit,
  HabitSchedule,
  RepeatingTask,
  ScheduleEntryState,
  Timestamp,
} from '@griply/types';
import { endOfDay, isAfter, isBefore, isSameDay } from 'date-fns';

import { getScheduleFrequencyDayTarget } from './get-schedule-frequency-day-target';
import { isNumber } from './is-number';
import {
  getPrecedingScheduleEndDate,
  getScheduleEndDate,
  isDegenerateSchedule,
  normalizeSchedules,
  updateScheduleEntryForDate,
} from './schedules';

export type ComputeHabitEntryUpdateOptions = {
  today: Timestamp;
  type?: ScheduleEntryState;
  value?: number;
  increment?: number;
  toggle?: boolean;
};

export type ComputeHabitEntryUpdateResult = {
  newSchedules: HabitSchedule[];
  shouldCompleteHabit: boolean;
  shouldUncompleteHabit: boolean;
  ignored: boolean;
};

export const computeHabitEntryUpdate = (
  habit: Habit | RepeatingTask,
  date: Timestamp,
  {
    today,
    type = ScheduleEntryState.Complete,
    value,
    increment,
    toggle,
  }: ComputeHabitEntryUpdateOptions,
): ComputeHabitEntryUpdateResult => {
  if (isAfter(date, endOfDay(today))) {
    return {
      newSchedules: habit.schedules,
      shouldCompleteHabit: false,
      shouldUncompleteHabit: false,
      ignored: true,
    };
  }

  let shouldCompleteHabit = false;
  let shouldUncompleteHabit = false;

  // Get the current entry value before updating to check if threshold will be reached
  const sortedSchedules = normalizeSchedules(
    [...habit.schedules].sort(
      (scheduleA, scheduleB) =>
        scheduleA.startDate.getTime() - scheduleB.startDate.getTime(),
    ),
  );

  // Find the schedule that applies to the date
  const scheduleForDate = sortedSchedules.find((schedule, index) => {
    if (isDegenerateSchedule(schedule)) {
      return false;
    }
    const startPointSchedule =
      getPrecedingScheduleEndDate(sortedSchedules, index) ?? new Date(0);
    return (
      !isBefore(date, startPointSchedule) &&
      (!schedule.endDate || !isAfter(date, getScheduleEndDate(schedule)!))
    );
  });

  if (scheduleForDate && type === ScheduleEntryState.Complete) {
    const entry = scheduleForDate.entries.find((entry) =>
      isSameDay(entry.date, date),
    );
    const entryVal = entry?.value ?? 0;

    // Calculate the new value that will be set
    const incrementValue = increment ?? 1;
    const newValue = Math.max(
      0,
      value ??
        (isNumber(increment)
          ? entryVal + incrementValue
          : (!scheduleForDate.frequency &&
                entryVal >= scheduleForDate.completionTarget.count) ||
              (!!scheduleForDate.frequency &&
                entryVal &&
                (getScheduleFrequencyDayTarget(scheduleForDate) ||
                  entryVal >= scheduleForDate.frequency.count)) ||
              (toggle && !!entryVal)
            ? 0
            : entryVal + 1),
    );

    // Check if completion threshold will be reached
    if (
      scheduleForDate.frequency
        ? entryVal < scheduleForDate.frequency.count &&
          newValue >=
            (getScheduleFrequencyDayTarget(scheduleForDate) ??
              scheduleForDate.frequency.count)
        : entryVal < scheduleForDate.completionTarget.count &&
          newValue >= scheduleForDate.completionTarget.count
    ) {
      shouldCompleteHabit = true;
    }

    // Check if a previously reached threshold is being reset
    const dayTarget = scheduleForDate.frequency
      ? getScheduleFrequencyDayTarget(scheduleForDate) ??
        scheduleForDate.frequency.count
      : scheduleForDate.completionTarget.count;
    if (entryVal >= dayTarget && newValue === 0) {
      shouldUncompleteHabit = true;
    }
  }

  // Use the helper function to update schedules
  const newSchedules = updateScheduleEntryForDate(habit, date, {
    today,
    type,
    value,
    increment,
    toggle,
  });

  return {
    newSchedules,
    shouldCompleteHabit,
    shouldUncompleteHabit,
    ignored: false,
  };
};
